import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import colors from '../../styles/colors';
import { useAppSelector } from '../../store/hooks';
import { apiGetMyCreditHistory } from '../../api/userCardApi';
import type { CreditHistoryEntry } from '../../api/userCardApi';

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: gold;
  margin: 0 0 28px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tr = styled.tr`
  &:hover td {
    background-color: rgba(255, 255, 255, 0.03);
  }
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const AmountCell = styled(Td)<{ $positive: boolean }>`
  font-weight: 700;
  color: ${({ $positive }) => ($positive ? '#4caf6f' : '#e05555')};
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 48px;
  font-size: 15px;
`;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const CreditHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );
  const [entries, setEntries] = useState<CreditHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/carte');
      return;
    }
    apiGetMyCreditHistory()
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setIsLoading(false));
  }, [isAuthenticated, navigate]);

  return (
    <CommonWrapper>
      <PageInner>
        <PageTitle>Storico Movimenti</PageTitle>

        {isLoading && <StatusText>Caricamento...</StatusText>}

        {!isLoading && entries.length === 0 && (
          <StatusText>Nessun movimento registrato.</StatusText>
        )}

        {!isLoading && entries.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Data</Th>
                <Th>Importo</Th>
                <Th>Note</Th>
                <Th>Admin</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <Tr key={e.id}>
                  <Td>{formatDate(e.created_at)}</Td>
                  <AmountCell $positive={e.amount > 0}>
                    {e.amount > 0 ? '+' : ''}{e.amount}
                  </AmountCell>
                  <Td>{e.notes ?? '—'}</Td>
                  <Td style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {e.granted_by ? e.granted_by.name : '—'}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </PageInner>
    </CommonWrapper>
  );
};

export default CreditHistoryPage;
